import { useState } from 'react';

import { useIsMobile } from '../../shared/hooks/useMediaQuery';
import { isInstallDismissed, writeInstallDismissed } from '../../shared/pwa/install-dismissal';
import { useInstallPrompt } from '../../shared/pwa/useInstallPrompt';
import { Button } from '../../shared/ui/Button';
import { IconButton } from '../../shared/ui/IconButton';

import s from './InstallBanner.module.css';

/**
 * "Add to home screen", offered once and quietly.
 *
 * Only on a phone, only in a browser tab (not once installed), and only where
 * there is something to do: Chromium hands us a prompt to show; iOS Safari
 * can only be told where its own control is. Waved away = gone for a
 * fortnight. Lives inside the app shell, so exam routes never see it.
 */
export function InstallBanner(): JSX.Element | null {
  const isMobile = useIsMobile();
  const { isStandalone, platform, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => isInstallDismissed(Date.now()));

  if (!isMobile || isStandalone || platform === 'none' || dismissed) return null;

  const dismiss = (): void => {
    writeInstallDismissed(Date.now());
    setDismissed(true);
  };

  const install = (): void => {
    void promptInstall().then((outcome) => {
      if (outcome === 'accepted') setDismissed(true);
      else if (outcome === 'dismissed') dismiss();
    });
  };

  return (
    <aside className={s.root} aria-label="Install app">
      <span className={s.mark} aria-hidden="true">
        i
      </span>
      <p className={s.text}>
        {platform === 'native'
          ? 'Install the app for quicker access and offline practice.'
          : 'Tap Share, then "Add to Home Screen" to install the app.'}
      </p>
      {platform === 'native' ? (
        <Button size="sm" className={s.action} onClick={install}>
          Install
        </Button>
      ) : null}
      <IconButton label="Dismiss" className={s.dismiss} onClick={dismiss}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </IconButton>
    </aside>
  );
}
